// client/src/dnd/emitter_tool/engine/RotationManager.js

/**
 * RotationManager: visual-only per-sprite rotation step.
 * Applies a simple angular velocity to each sprite.
 *
 * Effect settings supported:
 *  - rotateEnabled: boolean
 *  - rotateDirection: "cw" | "ccw"
 *  - rotateSpeedAbsDps: number (degrees per second, positive)
 *  - rotateSpeedDegPerS: number (signed degrees per second; overrides abs+dir if finite)
 */

const DEG = Math.PI / 180;

/** Derive signed radians/sec from effect settings. */
function getRotationSpeedRadS(es) {
  if (!es?.rotateEnabled) return 0;

  // Prefer signed if provided:
  const signedDps = Number(es?.rotateSpeedDegPerS);
  if (Number.isFinite(signedDps)) return signedDps * DEG;

  // Otherwise derive from abs + direction:
  const abs = Math.max(0, Number(es?.rotateSpeedAbsDps ?? 0));
  const dir = (es?.rotateDirection || "ccw") === "ccw" ? 1 : -1; // CCW positive
  return (dir * abs) * DEG;
}

/**
 * Apply rotation to sprites contained by a SimpleEmitter.
 * This does not require PIXI imports; it only assumes children have a numeric `rotation`.
 *
 * @param {object} emitter - SimpleEmitter instance with `.container`.
 * @param {object} effectSettings - Effect settings (see supported keys above).
 * @param {number} dt - Delta time in seconds.
 */
export function applyRotationToEmitter(emitter, effectSettings, dt) {
  const es = effectSettings || {};
  if (!es.rotateEnabled) return;

  const cont = emitter?.container;
  if (!cont || !cont.children || cont.children.length === 0) return;

  const wRadS = getRotationSpeedRadS(es);
  if (!wRadS) return;

  const delta = wRadS * (Number.isFinite(dt) ? dt : 0);
  if (!delta) return;

  for (let i = 0; i < cont.children.length; i++) {
    const sp = cont.children[i];
    if (!sp || !sp.visible) continue;

    // Only rotate if the child has a numeric rotation property
    const r = sp.rotation;
    if (typeof r === "number") {
      sp.rotation = r + delta;
    }
  }
}

export default {
  applyRotationToEmitter,
};
